import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** When true, only requires a signed-in account (no entitlement check). */
  authOnly?: boolean;
}

export const ProtectedRoute = ({ children, authOnly = false }: ProtectedRouteProps) => {
  const { subscribed, isTrialing, isFreeExploring, loading, user } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const hasAccess = authOnly || subscribed || isTrialing || isFreeExploring;

  useEffect(() => {
    if (loading) return;

    // Not signed in -> send to sign-up / sign-in, remembering where they wanted to go
    if (!user) {
      navigate('/auth', {
        replace: true,
        state: { from: location.pathname + location.search },
      });
      return;
    }

    // Signed in but out of access -> send to pricing
    if (!hasAccess) {
      navigate('/pricing', {
        replace: true,
        state: { message: 'Your free access has ended. Start your 14-day trial to continue.' },
      });
    }
  }, [loading, user, hasAccess, navigate, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user && hasAccess) {
    return <>{children}</>;
  }

  // Redirecting
  return null;
};

